import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AumicoCoreProvider, ReportType } from '@modeso/aumico-lib-core-fe';
import { IAnnualReport, IAnnualReportPdfRequest, IPrintOptions, ISetupData } from '@modeso/types__aumico-lib-annualreport-be';
import Debug from 'debug';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AumicoAnnualReportConfigService } from '../aumico-annualreport.config';
import { AnnualReportMetaInformationDTO } from '../dtos/annualReportMetaInformation.dto';
import { BulkCreateFinancialYearJobStatusDto } from '../dtos/bulk-create-financial-year-job-status.dto';
import { BulkCreateFinancialYearPreviewDto } from '../dtos/bulk-create-financial-year-preview.dto';
import { BulkCreateFinancialYearRequestDto } from '../dtos/bulk-create-financial-year-request.dto';
import { BulkCreateFinancialYearResponseDto } from '../dtos/bulk-create-financial-year-response.dto';
import { CreateAnnualReportResponseDto } from '../dtos/create-annual-report-response.dto';
import { IDispatchAnnualReportsFilter } from '../dtos/dispatch-annual-reports-filter.dto';
import { IAuditLogDTO } from '../dtos/get-audit-log.dto';
import { PrintUsingPdfTronDto } from '../dtos/print-using-pdf-tron.dto';
import { AnnualReportExcelSheet } from '../interfaces/export-excel/annual-report-excel-sheet.interface';
import { IPdfTronData } from '../interfaces/pdf-tron/pdf-tron-data.interface';
import { RoutesUtil } from '../utils/routes.util';

const debug = Debug('modeso:aumico-annualreport:AumicoCoreService');

const httpOptions = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json',
    }),
};

@Injectable()
export class AumicoAnnualReportService {
    private server = '';
    private microserviceName = 'annualreport';
    private microserviceNameInAumicoBe = 'projects';
    private readonly route = '/';
    private readonly printroutes = '/print/';

    constructor(
        private http: HttpClient,
        @Inject(AumicoAnnualReportConfigService) private config,
        private coreProvider: AumicoCoreProvider,
        private readonly routesUtil: RoutesUtil,
    ) {
        this.server = config.apiGateway;
    }

    createAnnualReport$(payload?: {
        setupData: ISetupData;
        existingAnnualReportEncryptedId?: string;
        existingProjectId?: string;
    }): Observable<CreateAnnualReportResponseDto> {
        debug('create new annual report');
        const url = `${this.server}${this.microserviceName}`;
        this.coreProvider.dispatchBlockingRequest('createAnnualReport$');
        return this.http
            .post<CreateAnnualReportResponseDto>(
                url,
                {
                    setupData: payload?.setupData,
                    existingAnnualReportEncryptedId: payload?.existingAnnualReportEncryptedId,
                    existingProjectId: payload?.existingProjectId,
                },
                httpOptions,
            )
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('createAnnualReport$')));
    }

    updateAnnualReport$(payload: { annualReport: IAnnualReport; annualReportEncryptedId: string }): Observable<void> {
        debug('updateAnnualReport called');
        const url = `${this.server}${this.microserviceName}`;
        this.coreProvider.dispatchBlockingRequest('updateAnnualReport$');
        return this.http
            .put<void>(
                url,
                {
                    annualReport: payload.annualReport,
                    annualReportEncryptedId: payload.annualReportEncryptedId,
                },
                httpOptions,
            )
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('updateAnnualReport$')));
    }

    getPreviousAnnualReportId$(payload: {
        companyId: string;
        currentAnnualReportId: string;
        legalFormId: string;
        accountingBasisId?: string;
        locale?: string;
    }): Observable<string> {
        let url = `${this.server}${this.microserviceName}${this.route}getPreviousId/${payload.companyId}/${payload.currentAnnualReportId}${this.route}${payload.legalFormId}`;

        if (payload.accountingBasisId != null && payload.locale != null) {
            url = `${url}${this.route}${payload.accountingBasisId}${this.route}${payload.locale}`;
        }

        this.coreProvider.dispatchBlockingRequest('getPreviousAnnualReportId$');
        return this.http
            .get<string>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getPreviousAnnualReportId$')));
    }

    getPreviousAnnualReportOptions$(payload: {
        companyId: string;
        currentAnnualReportId: string;
        legalFormId: string;
        accountingBasisId?: string;
        locale?: string;
    }): Observable<Partial<IAnnualReport>[]> {
        let url = `${this.server}${this.microserviceName}${this.route}getPreviousAnnualReportOptions/${payload.companyId}/${payload.currentAnnualReportId}/${payload.legalFormId}`;

        if (payload.accountingBasisId != null) {
            url = `${url}/${payload.accountingBasisId}`;
        }

        if (payload.locale != null) {
            url = `${url}/${payload.locale}`;
        }

        this.coreProvider.dispatchBlockingRequest('getPreviousAnnualReportOptions$');
        return this.http
            .get<Partial<IAnnualReport>[]>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getPreviousAnnualReportOptions$')));
    }


    getAnnualReportsByCompanyId$(payload: { payload: string }): Observable<IAnnualReport[]> {
        let url = `${this.server}${this.microserviceName}/companyId/${payload.payload}`;

        this.coreProvider.dispatchBlockingRequest('getAnnualReportsByCompanyId$');
        return this.http
            .get<IAnnualReport[]>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getAnnualReportsByCompanyId$')));
    }

    getAnnualReportByEncryptedId$(payload): Observable<IAnnualReport> {
        const annualReportEncryptedId = encodeURIComponent(`${payload.payload}`);
        const url = `${this.server}${this.microserviceName}${this.route}encryptedId${this.route}${annualReportEncryptedId}`;
        this.coreProvider.dispatchBlockingRequest('getAnnualReportById$');
        return this.http
            .get<IAnnualReport>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getAnnualReportById$')));
    }

    getAnnualReportById$(payload): Observable<IAnnualReport> {
        const url = `${this.server}${this.microserviceName}${this.route}${payload.payload}`;
        this.coreProvider.dispatchBlockingRequest('getAnnualReportById$');
        return this.http
            .get<IAnnualReport>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getAnnualReportById$')));
    }

    getActiveAnnualReports(payload: {
        payload: {
            filter?: IDispatchAnnualReportsFilter;
        };
    }): Observable<AnnualReportMetaInformationDTO[]> {
        const url = `${this.server}${this.microserviceName}/active`;

        let params = new HttpParams();
        if (
            payload != null &&
            payload.payload != null &&
            payload.payload.filter != null &&
            payload.payload.filter.companyName != null
        ) {
            params = params.set('companyName', payload.payload.filter.companyName);
        }
        if (
            payload != null &&
            payload.payload != null &&
            payload.payload.filter != null &&
            payload.payload.filter.onlyWithNotes != null
        ) {
            params = params.set('onlyWithNotes', payload.payload.filter.onlyWithNotes.toString());
        }
        this.coreProvider.dispatchBlockingRequest('getActiveAnnualReports$');
        return this.http
            .get<AnnualReportMetaInformationDTO[]>(url, { params })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getActiveAnnualReports$')));
    }

    getArchivedAnnualReports(payload: {
        payload: {
            filter?: IDispatchAnnualReportsFilter;
        };
    }): Observable<AnnualReportMetaInformationDTO[]> {
        const url = `${this.server}${this.microserviceName}/archived`;

        let params = new HttpParams();
        if (payload != null && payload.payload.filter != null && payload.payload.filter.companyName != null) {
            params = params.set('companyName', payload.payload.filter.companyName);
        }

        this.coreProvider.dispatchBlockingRequest('getArchivedAnnualReports$');
        return this.http
            .get<AnnualReportMetaInformationDTO[]>(url, { params })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getArchivedAnnualReports$')));
    }

    printAnnualReport$(print: IAnnualReportPdfRequest): Observable<any> {
        const url = `${this.server}${this.microserviceName}${this.printroutes}`;
        this.coreProvider.dispatchBlockingRequest('printAnnualReport$');
        return this.http
            .post(url, print, { responseType: 'blob', observe: 'body' })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('printAnnualReport$')));
    }

    printAnnualReportUsingPdfTron$(payload: PrintUsingPdfTronDto): Observable<any> {
        const url = this.routesUtil.reportingPdf();

        const filename = this.generateFilename(payload.data, 'pdf');

        this.coreProvider.dispatchBlockingRequest('printAnnualReportUsingPdfTron$');
        return this.http
            .post(url, { ...payload, filename }, { responseType: 'blob', observe: 'body' })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('printAnnualReportUsingPdfTron$')));
    }

    printAnnualReportUsingPdfTronV2$(payload: PrintUsingPdfTronDto): Observable<void> {
        const url = this.routesUtil.reportingPdfV2();

        // No filename handling needed here as we don't return a blob immediately

        this.coreProvider.dispatchBlockingRequest('printAnnualReportUsingPdfTronV2$');
        return this.http
            .post<void>(url, payload, { headers: httpOptions.headers })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('printAnnualReportUsingPdfTronV2$')));
    }

    /**
     * REV-21106 — POST a self-contained HTML document to the backend
     * and receive a rendered PDF blob back.
     *
     * Used by the reclassifications-preview "Export PDF" button: the
     * dialog already builds the exact HTML it shows on screen (inline
     * CSS, `@page` rules for A4 landscape) and ships it here. The BE
     * forwards the HTML to Gotenberg's Chromium route — no DOCX
     * template, no Pub/Sub round-trip, no WebSocket — and streams the
     * PDF back in the same response so the caller can trigger a
     * browser download immediately. Wraps the request in the standard
     * blocking-request scaffold so a global spinner is shown while
     * Gotenberg renders.
     */
    public exportHtmlToPdf$(html: string): Observable<Blob> {
        const url = this.routesUtil.reportingHtmlPdf();
        const requestKey = 'exportHtmlToPdf$';
        this.coreProvider.dispatchBlockingRequest(requestKey);
        return this.http
            .post(url, { html }, { responseType: 'blob', observe: 'body' })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd(requestKey)));
    }

    exportAnnualReportToWord$(payload: PrintUsingPdfTronDto): Observable<any> {
        const url = this.routesUtil.exportToWord();
        this.coreProvider.dispatchBlockingRequest('exportAnnualReportToWord$');
        return this.http
            .post(url, payload, { responseType: 'blob', observe: 'body' })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('exportAnnualReportToWord$')));
    }

    exportAnnualReportToWordV2$(payload: PrintUsingPdfTronDto): Observable<void> {
        const url = this.routesUtil.exportToWordV2();
        this.coreProvider.dispatchBlockingRequest('exportAnnualReportToWordV2$');
        return this.http
            .post<void>(url, payload, { headers: httpOptions.headers })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('exportAnnualReportToWordV2$')));
    }

    exportAnnualReportExcel$(payload: { sheetsData: AnnualReportExcelSheet[] }): Observable<any> {
        const url = this.routesUtil.exportExcel();
        this.coreProvider.dispatchBlockingRequest('exportAnnualReportExcel$');
        return this.http
            .post(url, payload, { responseType: 'blob', observe: 'body' })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('exportAnnualReportExcel$')));
    }

    exportAnnualReportExcelV2$(payload: { sheets: AnnualReportExcelSheet[] }): Observable<void> {
        const url = this.routesUtil.exportExcelV2();
        this.coreProvider.dispatchBlockingRequest('exportAnnualReportExcelV2$');
        return this.http
            .post<void>(url, payload, { headers: httpOptions.headers })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('exportAnnualReportExcelV2$')));
    }

    exportAnnualReportToExcelStructured$(payload: PrintUsingPdfTronDto): Observable<any> {
        const url = this.routesUtil.exportExcelStructured();
        this.coreProvider.dispatchBlockingRequest('exportAnnualReportToExcelStructured$');
        return this.http
            .post(url, payload, { responseType: 'blob', observe: 'body' })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('exportAnnualReportToExcelStructured$')));
    }

    exportAnnualReportToExcelStructuredV2$(payload: PrintUsingPdfTronDto): Observable<void> {
        const url = this.routesUtil.exportExcelStructuredV2();
        this.coreProvider.dispatchBlockingRequest('exportAnnualReportToExcelStructuredV2$');
        return this.http
            .post<void>(url, payload, { headers: httpOptions.headers })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('exportAnnualReportToExcelStructuredV2$')));
    }

    archiveAnnualReports$(payload: { annualReportIds: string[] }): Observable<void> {
        debug('archiveAnnualReports called');
        const url = `${this.server}${this.microserviceName}/archive`;
        this.coreProvider.dispatchBlockingRequest('archiveAnnualReports$');
        return this.http
            .put<void>(
                url,
                {
                    ids: payload.annualReportIds,
                },
                httpOptions,
            )
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('archiveAnnualReports$')));
    }

    restoreAnnualReports$(payload: { annualReportIds: string[] }): Observable<void> {
        debug('restoreAnnualReports called');
        const url = `${this.server}${this.microserviceName}/restore`;
        this.coreProvider.dispatchBlockingRequest('restoreAnnualReports$');
        return this.http
            .put<void>(
                url,
                {
                    ids: payload.annualReportIds,
                },
                httpOptions,
            )
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('restoreAnnualReports$')));
    }

    deleteAnnualReports$(payload: { annualReportIds: string[] }): Observable<void> {
        debug('deleteAnnualReports called');
        const url = `${this.server}${this.microserviceName}/delete`;
        this.coreProvider.dispatchBlockingRequest('deleteAnnualReports$');

        return this.http
            .put<void>(
                url,
                {
                    ids: payload.annualReportIds,
                },
                httpOptions,
            )
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('deleteAnnualReports$')));
    }

    sealAnnualReport$(activeAnnualReportEncryptedId: string): Observable<void> {
        const annualReportEncryptedId = encodeURIComponent(`${activeAnnualReportEncryptedId}`);
        const url = `${this.server}${this.microserviceName}/${annualReportEncryptedId}/seal`;
        this.coreProvider.dispatchBlockingRequest('sealAnnualReport$');
        return this.http
            .patch<void>(url, {}, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('sealAnnualReport$')));
    }

    unsealAnnualReport$(activeAnnualReportEncryptedId: string): Observable<void> {
        const annualReportEncryptedId = encodeURIComponent(`${activeAnnualReportEncryptedId}`);
        const url = `${this.server}${this.microserviceName}/${annualReportEncryptedId}/unseal`;

        this.coreProvider.dispatchBlockingRequest('unsealAnnualReport$');
        return this.http
            .patch<void>(url, {}, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('unsealAnnualReport$')));
    }

    updatePrintOptions$(activeAnnualReportEncryptedId: string, printOptions: IPrintOptions): Observable<void> {
        const annualReportEncryptedId = encodeURIComponent(`${activeAnnualReportEncryptedId}`);
        const url = `${this.server}${this.microserviceName}/${annualReportEncryptedId}/print-options`;

        this.coreProvider.dispatchBlockingRequest('updatePrintOptions$');
        return this.http
            .patch<void>(url, { printOptions }, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('updatePrintOptions$')));
    }

    getAnnualReportAuditLogs$(activeAnnualReportEncryptedId: string): Observable<IAuditLogDTO[]> {
        const annualReportEncryptedId = encodeURIComponent(`${activeAnnualReportEncryptedId}`);
        const url = `${this.server}auditlogs/entity/${annualReportEncryptedId}`;
        this.coreProvider.dispatchBlockingRequest('getAnnualReportAuditLogs$');
        return this.http
            .get<IAuditLogDTO[]>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getAnnualReportAuditLogs$')));
    }

    getNextBalanceSheetDateForCompanyById$(companyId: string, reportType: ReportType): Observable<Date> {
        let url = `${this.server}${this.microserviceName}/balance-sheet-date/${reportType}/${companyId}`;

        this.coreProvider.dispatchBlockingRequest('getNextBalanceSheetDateForCompanyById$');
        return this.http
            .get<Date>(url)
            .pipe(
                finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getNextBalanceSheetDateForCompanyById$')),
            );
    }

    private generateFilename(data: IPdfTronData | null, extension: string): string {
        const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD format

        if (!data || !data.customer || !data.cover) {
            // Fallback to timestamp-based name if data is not available
            return `AnnualReport_${today}.${extension}`;
        }

        const companyName = (data.customer.companyName || 'Company')
            .replace(/[^a-zA-Z0-9]/g, '_') // Replace non-alphanumeric characters with underscores
            .replace(/_+/g, '_') // Replace multiple underscores with single underscore
            .replace(/^_|_$/g, ''); // Remove leading/trailing underscores

        const reportingPeriod = (data.cover.reportingPeriod || 'Period')
            .replace(/[^a-zA-Z0-9]/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_|_$/g, '');

        return `${companyName}_${reportingPeriod}_${today}.${extension}`;
    }

    bulkCreateFinancialYearPreview$(
        payload: BulkCreateFinancialYearRequestDto,
    ): Observable<BulkCreateFinancialYearPreviewDto> {
        debug('bulk create financial year preview');
        const url = `${this.server}${this.microserviceNameInAumicoBe}/bulk-create-financial-year/preview`;
        this.coreProvider.dispatchBlockingRequest('bulkCreateFinancialYearPreview$');
        return this.http
            .post<BulkCreateFinancialYearPreviewDto>(url, payload, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('bulkCreateFinancialYearPreview$')));
    }

    bulkCreateFinancialYear$(
        payload: BulkCreateFinancialYearRequestDto,
    ): Observable<BulkCreateFinancialYearResponseDto> {
        debug('bulk create financial year');
        const url = `${this.server}${this.microserviceNameInAumicoBe}/bulk-create-financial-year`;
        return this.http
            .post<BulkCreateFinancialYearResponseDto>(url, payload, httpOptions);

    }

    getBulkCreateFinancialYearStatus$(jobId: string): Observable<BulkCreateFinancialYearJobStatusDto> {
        debug('get bulk create financial year status');
        const url = `${this.server}${this.microserviceNameInAumicoBe}/bulk-create-financial-year/status/${jobId}`;
        // Note: No blocking request dispatch for polling endpoints to avoid blocking UI
        return this.http.get<BulkCreateFinancialYearJobStatusDto>(url);
    }
}
